import { useMemo } from "react";
import { FoodDto, PurchaseDto } from "../../api";
import { FoodUnit } from "../../data/foodUnit";
import { MealIngredient } from "../../data/mealWithIngredients";
import { MealWithIngredients } from "../../data/mealWithIngredients";
import { NutriValues, emptyNutriValue } from "../../data/nutriValues";
import {
  isNullIndex,
  nutriValueSumReducer,
  nutriValuesFromFoodUnitWithAmount,
  nutriValuesFromMealIngredient,
} from "../../utils/planUtils";
import { collectFoodIds } from "../../utils/foodIdUtils";
import { formatPurchaseDescription } from "../../utils/purchaseUtils";
import {
  useFoodAsync,
  useFoodByUnitAsync,
  useFoodsByIdsAsync,
  useMealPurchasesAsync,
  usePurchaseAsync,
  usePurchasesByIdsAsync,
  usePurchasesByIdsState,
  usePurchasesByFoodAsync,
} from "../../queries/caches";
import { IngredientEdit, IngredientMeta, PlannedEdit } from "./plannedIntakeUtils";
import { IntakeWithEditedFlags } from "../../data/plan";
import { isDeleted } from "../../utils/editUtils";
import { nutriValuesFromIntake } from "../../utils/planUtils";

/**
 * Per-Entity-Lookup der Zutaten-Daten. Statt vorab Ids zu sammeln und zu
 * "finden", lädt jede Zutat genau das, was sie braucht – fehlt etwas, wird das
 * Fetch-Promise geworfen und die umgebende `QueryBoundary` zeigt den Ladezustand.
 */
export function useIngredientMeta(
  mi: MealIngredient,
  edit?: IngredientEdit,
): {
  meta: IngredientMeta;
  food: FoodDto | undefined;
  purchase: PurchaseDto | undefined;
} {
  const isFoodBased = !isNullIndex(mi.foodUnitFoodId);

  // Purchase dieser Zutat: bei purchase-based der Plan-Purchase, bei
  // food-based der ggf. gewählte Purchase (`edit.purchaseId`).
  const planPurchase = usePurchaseAsync(
    !isFoodBased ? mi.purchaseUnitPurchaseId : undefined,
  );
  const selectedPurchase = usePurchaseAsync(edit?.purchaseId ?? undefined);
  const purchase = selectedPurchase ?? planPurchase;
  const hasSelectedPurchase = edit?.purchaseId != null;

  // `edit.unitId` ist eine FoodUnit-Id NUR wenn kein Purchase gewählt ist.
  // Bei gewähltem Purchase ist es eine PurchaseUnit-Id und darf nicht als
  // FoodUnit geladen werden.
  const selectedFoodUnitId =
    isFoodBased && !hasSelectedPurchase && edit?.unitId != null
      ? edit.unitId
      : undefined;
  const alternativeFood = useFoodByUnitAsync(
    selectedFoodUnitId !== undefined && selectedFoodUnitId !== mi.foodUnitId
      ? selectedFoodUnitId
      : undefined,
  );
  const purchaseFood = useFoodAsync(
    isFoodBased && hasSelectedPurchase ? purchase?.foodId : undefined,
  );
  const planFood = useFoodAsync(isFoodBased ? mi.foodUnitFoodId : undefined);
  const food = alternativeFood ?? purchaseFood ?? planFood;

  const foodId = isFoodBased ? food?.id : purchase?.foodId;
  const purchases = usePurchasesByFoodAsync(foodId);
  const meta = useMemo(
    () => buildIngredientMeta(mi, food, purchase, purchases),
    [mi, food, purchase, purchases],
  );
  return { meta, food, purchase };
}

export function buildIngredientMeta(
  mi: MealIngredient,
  food: FoodDto | undefined,
  purchase: PurchaseDto | undefined,
  purchases: PurchaseDto[],
): IngredientMeta {
  const isFoodBased = !isNullIndex(mi.foodUnitFoodId);
  if (isFoodBased) {
    return {
      key: String(mi.id),
      name: food?.description || mi.foodUnitFoodDescription || "",
      isFoodBased,
      plannedAmount: mi.amount,
      planUnitId: mi.foodUnitId!,
      planPurchaseId: null,
      defaultPurchaseId: food?.defaultPurchaseId ?? null,
      planUnits: (food?.foodUnits ?? []) as FoodUnit[],
      purchases,
    };
  }
  return {
    key: String(mi.id),
    name:
      formatPurchaseDescription(purchase) ||
      mi.purchaseUnitPurchaseDescription ||
      "",
    isFoodBased,
    plannedAmount: mi.amount,
    planUnitId: mi.purchaseUnitId!,
    planPurchaseId: mi.purchaseUnitPurchaseId ?? null,
    defaultPurchaseId: null,
    planUnits: (purchase?.purchaseUnits ?? []).map((pu) => ({
      id: pu.id,
      description: pu.description,
      netWeight: pu.netWeight,
      isDefault: pu.isDefault,
    })),
    purchases,
  };
}

/**
 * Nährwerte einer geplanten Mahlzeit. Die Ids werden ausschließlich aus dieser
 * Mahlzeit abgeleitet (kein globales Sammeln) und über die (suspenden)
 * Batch-Lookups aufgelöst.
 */
export function useMealNutri(meal: MealWithIngredients): NutriValues {
  const meals = useMemo(() => [meal], [meal]);
  const purchases = useMealPurchasesAsync(meals);
  const foodIds = useMemo(
    () => collectFoodIds(meals, purchases),
    [meals, purchases],
  );
  const foods = useFoodsByIdsAsync(foodIds);
  return useMemo(() => {
    const values = meal.mealIngredients.map((mi) =>
      nutriValuesFromMealIngredient(mi, foods, purchases),
    );
    return values.length > 0
      ? values.reduce(nutriValueSumReducer)
      : emptyNutriValue;
  }, [meal, foods, purchases]);
}

/**
 * Nährwerte bereits verzehrter Einträge (per-Entity-Purchase-Lookups). Nimmt die
 * Intakes direkt entgegen, damit sowohl geplante Meals (`cell.intakes`) als auch
 * reine Intakes (`[cell]`) darüber berechnet werden können.
 */
export function useConsumedNutri(intakes: IntakeWithEditedFlags[]): NutriValues {
  const purchaseIds = useMemo(
    () =>
      intakes
        .filter((i) => !isDeleted(i))
        .map((i) => i.purchaseUnitPurchaseId),
    [intakes],
  );
  const { data: purchases, isLoading } =
    usePurchasesByIdsState(purchaseIds);
  return useMemo(() => {
    const active = intakes.filter(
      (i) => !isDeleted(i) && (i.amount || 0) > 0,
    );
    // Solange die Purchases laden, ist `purchases` unvollständig – nicht als
    // „nichts gefunden" deuten (sonst falsche Lookup-Misses).
    if (active.length === 0 || isLoading) return { ...emptyNutriValue };
    return active.reduce(
      (acc, i) => nutriValueSumReducer(acc, nutriValuesFromIntake(i, purchases)),
      { ...emptyNutriValue },
    );
  }, [intakes, purchases, isLoading]);
}

function nutriFromPurchase(
  purchases: PurchaseDto[],
  purchaseId: number,
  unitId: number,
  amount: number,
): NutriValues {
  const purchase = purchases.find((p) => p.id === purchaseId);
  if (!purchase) return { ...emptyNutriValue };
  const purchaseUnit =
    purchase.purchaseUnits.find((u) => u.id === unitId) ??
    purchase.purchaseUnits[0];
  if (!purchaseUnit) return { ...emptyNutriValue };
  const factor =
    (purchaseUnit.netWeight != null ? purchaseUnit.netWeight / 100 : 1) *
    amount;
  return {
    fat: purchase.fat * factor,
    carbs: purchase.carbs * factor,
    protein: purchase.protein * factor,
    kcal: (purchase.kcal ?? 0) * factor,
  };
}

/**
 * Nährwerte einer geplanten Mahlzeit **mit den live editierten Werten** (Menge,
 * Einheit, Alternative, gewähltes Produkt, gelöschte Zutaten). Grundlage für die
 * Makro-Leiste im Verzehr-Kopf.
 */
export function useMealEditNutri(
  meal: MealWithIngredients,
  edit: PlannedEdit,
): NutriValues {
  const { foodUnitIds, purchaseIds } = useMemo(() => {
    const foodUnitIds: number[] = [];
    const purchaseIds: number[] = [];
    for (const mi of meal.mealIngredients) {
      const e = edit.ingredients[String(mi.id)];
      if (e?.deleted) continue;
      const isFoodBased = !isNullIndex(mi.foodUnitFoodId);
      if (isFoodBased) {
        if (e?.purchaseId != null) {
          purchaseIds.push(e.purchaseId);
        } else {
          const unitId = e?.unitId ?? mi.foodUnitId;
          if (!isNullIndex(unitId)) foodUnitIds.push(unitId);
        }
      } else {
        const purchaseId = e?.purchaseId ?? mi.purchaseUnitPurchaseId;
        if (purchaseId != null && !isNullIndex(purchaseId)) {
          purchaseIds.push(purchaseId);
        }
      }
    }
    return { foodUnitIds, purchaseIds };
  }, [meal, edit]);

  const foods = useFoodsByIdsAsync([], foodUnitIds);
  const purchases = usePurchasesByIdsAsync(purchaseIds);

  return useMemo(() => {
    let acc = { ...emptyNutriValue };
    for (const mi of meal.mealIngredients) {
      const e = edit.ingredients[String(mi.id)];
      if (e?.deleted) continue;
      const amount = e?.amount ?? mi.amount;
      if (amount <= 0) continue;
      const isFoodBased = !isNullIndex(mi.foodUnitFoodId);

      let value: NutriValues;
      if (isFoodBased && e?.purchaseId == null) {
        value = nutriValuesFromFoodUnitWithAmount(
          foods,
          e?.unitId ?? mi.foodUnitId ?? undefined,
          amount,
        );
      } else if (isFoodBased) {
        value = nutriFromPurchase(
          purchases,
          e!.purchaseId!,
          e?.unitId ?? -1,
          amount,
        );
      } else {
        value = nutriFromPurchase(
          purchases,
          e?.purchaseId ?? mi.purchaseUnitPurchaseId ?? -1,
          e?.unitId ?? mi.purchaseUnitId ?? -1,
          amount,
        );
      }
      acc = nutriValueSumReducer(acc, value);
    }
    return acc;
  }, [meal, edit, foods, purchases]);
}
